import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormsModule,
  FormBuilder,
  FormGroup,
  FormArray,
  FormControl,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { TextareaModule } from 'primeng/textarea';

import { TooltipModule } from 'primeng/tooltip';
import { SkeletonModule } from 'primeng/skeleton';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, of, switchMap } from 'rxjs';
import {
  EquipamentoService,
  TipoEquipService,
  PostoService,
  PropriedadeEquipService,
} from '@core/services/domain-services';
import {
  DataSourceRegistryService,
  PROPRIEDADE_OPCOES_SOURCE_KEY,
} from '@core/services/data-source-registry.service';
import {
  QuickCreateOptionComponent,
  QuickCreateOptionResult,
} from '../components/quick-create-option/quick-create-option.component';
import {
  DropdownOption,
  EnumEstadoEquipamento,
  EnumMarcaCategoria,
  Equipamento,
  EquipamentoPropriedadeValor,
  Posto,
  PropriedadeEquipamento,
  TipoEquipamento,
} from '@core/models/models';
import { EnumService } from '@shared/api/services/enum.service';
import { BackButtonComponent } from '@shared/components/back-button/back-button.component';
import { formatNomeCampo } from '@shared/utils/format-nome-campo.util';
import { EquipmentPhotoComponent } from '../components/equipment-photo/equipment-photo.component';
import { EquipmentPhotoService } from '../services/equipment-photo.service';
import { compressImageIfNeeded } from '@shared/utils/compress-image.util';
import { environment } from '@environments/environment';
import { CALIBRES_OPTIONS } from '@shared/constants/dropdown-static-data';

interface PendingDocumento {
  file: File | null;
  designacao: string;
}

interface PendingManutencao {
  tipo: number;
  data: Date | null;
  tecnico: string;
  descricao: string;
  custo: number | null;
}

interface DynamicPropertyItem {
  ctrl: AbstractControl;
  index: number;
}

@Component({
  selector: 'app-novo-equipamento',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    DatePickerModule,
    InputNumberModule,
    TextareaModule,

    TooltipModule,
    SkeletonModule,
    BackButtonComponent,
    QuickCreateOptionComponent,
    EquipmentPhotoComponent,
  ],

  templateUrl: './novo-equipamento.component.html',
  styleUrl: './novo-equipamento.component.scss',
})
export class NovoEquipamentoComponent implements OnInit {
  @Input() drawerMode = false;
  @Input() presetTipoId: number | null = null;
  @Input() IdCategoriaEquipamento: number | null = null;
  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<Equipamento>();

  private readonly fb = inject(FormBuilder);
  readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly msg = inject(MessageService);
  private readonly svc = inject(EquipamentoService);
  private readonly tipoSvc = inject(TipoEquipService);
  private readonly enumSvc = inject(EnumService);
  private readonly postoSvc = inject(PostoService);
  private readonly propriedadeSvc = inject(PropriedadeEquipService);
  private readonly dataSourceRegistry = inject(DataSourceRegistryService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly equipmentPhotoService = inject(EquipmentPhotoService);

  form!: FormGroup;
  loading = false;
  private submitted = false;
  loadingDropdowns = false;
  loadingPropriedades = false;
  editMode = false;
  editId: number | null = null;
  tipoPreSelecionado = false;

  tiposEquipamento: TipoEquipamento[] = [];
  categorias: Array<DropdownOption & { value: EnumMarcaCategoria }> = [];
  postos: Posto[] = [];
  today = new Date();
  propOptions: DropdownOption[][] = [];
  propOptionsLoading: boolean[] = [];
  propDropdownDisabled: boolean[] = [];
  marcasUtensilio: DropdownOption[] = [];
  modelosUtensilio: DropdownOption[] = [];
  loadingMarcasUtensilio = false;
  loadingModelosUtensilio = false;

  get tipoSelecionadoLabel(): string {
    const id = this.form?.getRawValue()?.idTipoEquipamento;
    return (
      this.tiposEquipamento.find((t) => t.id === id)?.designacao?.trim() ?? ''
    );
  }

  selectedFoto: File | null = null;
  fotoPreviewUrl: string | null = null;
  private fotoUrlOriginal: string | null = null;

  pendingDocumentos: PendingDocumento[] = [];
  pendingManutencoes: PendingManutencao[] = [];

  readonly estados = [
    { label: 'Bom', value: EnumEstadoEquipamento.BOM },
    { label: 'Inactivo', value: EnumEstadoEquipamento.INATIVO },
    { label: 'Destruído', value: EnumEstadoEquipamento.DESTRUIDO },
  ];

  readonly tiposManutencao = [
    { label: 'Revisão', value: 0 },
    { label: 'Reparação', value: 1 },
    { label: 'Calibração', value: 2 },
    { label: 'Inspecção', value: 3 },
  ];

  get propriedadesFormArray(): FormArray {
    return this.form.get('propriedades') as FormArray;
  }

  get isViatura(): boolean {
    return (
      this.form?.get('tipoEquipamentoEnum')?.value ===
      EnumMarcaCategoria.VEICULO
    );
  }

  get isArma(): boolean {
    return (
      this.form?.get('tipoEquipamentoEnum')?.value ===
      EnumMarcaCategoria.ARMA
    );
  }

  get isUtensilio(): boolean {
    return this.form?.get('tipoEquipamentoEnum')?.value === EnumMarcaCategoria.UTENSILIO;
  }

  readonly viaturaLabels: Record<string, string> = {
    MATRICULA: 'Matrícula',
    MARCA: 'Marca',
    MODELO: 'Modelo',
    ANO_FABRICO: 'Ano Fabrico',
    COR: 'Cor',
    NUMERO_CHASSI: 'Número Chassi',
    NUMERO_MOTOR: 'Número Motor',
    TIPO_COMBUSTIVEL: 'Tipo Combustível',
    KM_INICIAL: 'Km Inicial',
    ESTADO_INICIAL: 'Estado Inicial',
    DISPOSITIVO_GPS: 'Dispositivo GPS',
    DATA_ULTIMA_REVISAO: 'Data Última Revisão',
    KM_ULTIMA_REVISAO: 'Km Última Revisão',
    PROXIMA_REVISAO_DATA: 'Próxima Revisão (Data)',
    PROXIMA_REVISAO_KM: 'Próxima Revisão (Km)',
    OFICINA_PREFERENCIAL: 'Oficina Preferencial',
    TELEFONE_OFICINA: 'Telefone Oficina',
    SEGURADORA: 'Seguradora',
    NUMERO_APOLICE_SEGURO: 'Número Apólice Seguro',
    VALIDADE_SEGURO: 'Validade Seguro',
    VALOR_ANUAL_SEGURO: 'Valor Anual Seguro',
    FRENTE: 'Frente',
    MOTORISTA_RESPONSAVEL: 'Motorista Responsável',
    SIGLA: 'Sigla',
    DETALHES: 'Detalhes',
    UA_C_CUSTOS: 'UA C Custos',
    CODIGO_GRUPO: 'Código Grupo',
    CATEGORIA_EQUIPAMENTO_VIATURA: 'Categoria Equipamento Viatura',
  };

  readonly viaturaProps = {
    identificacao: [
      'MATRICULA',
      'MARCA',
      'MODELO',
      'ANO_FABRICO',
      'COR',
      'NUMERO_CHASSI',
      'NUMERO_MOTOR',
      'TIPO_COMBUSTIVEL',
      'KM_INICIAL',
      'DISPOSITIVO_GPS',
    ],

    manutencao: [
      'DATA_ULTIMA_REVISAO',
      'KM_ULTIMA_REVISAO',
      'PROXIMA_REVISAO_DATA',
      'PROXIMA_REVISAO_KM',
      'OFICINA_PREFERENCIAL',
      'TELEFONE_OFICINA',
    ],

    seguro: [
      'SEGURADORA',
      'NUMERO_APOLICE_SEGURO',
      'VALIDADE_SEGURO',
      'VALOR_ANUAL_SEGURO',
    ],

    responsavel: [
      'FRENTE',
      'MOTORISTA_RESPONSAVEL',
      'SIGLA',
      'UA_C_CUSTOS',
      'CODIGO_GRUPO',
      'CATEGORIA_EQUIPAMENTO_VIATURA',
    ],
  };

  readonly armaProps = {
    identificacaoAntes: ['NUMERO_SERIE', 'TIPO'],
    identificacaoDepois: [
      'MARCA',
      'MODELO',
      'CALIBRE',
      'PAIS_ORIGEM',
      'ANO_FABRICO',
      'COR_ACABAMENTO',
      'CAPACIDADE_MUNICOES',
    ],
    documentacao: [
      'NUMERO_LICENCA',
      'DATA_EMISSAO_LICENCA',
      'VALIDADE_LICENCA',
      'ENTIDADE_EMISSORA',
    ],
    categoria: ['CATEGORIA_EQUIPAMENTO_ARMA'],
    atribuicao: [
      'GUARDA_RESPONSAVEL',
      'DATA_ULTIMA_VERIFICACAO',
      'FREQUENCIA_VERIFICACAO',
      'PROXIMA_VERIFICACAO_OBRIGATORIA',
    ],
  };

  readonly utensilioProps = {
    dados: ['NOME_ITEM', 'MARCA', 'MODELO', 'CATEGORIA'],
    resumo: ['RESPONSAVEL'],
    administrativos: ['NUMERO_SERIE', 'MATRICULA', 'QUANTIDADE'],
  };

  getPropsForGroup(groupKeys: string[]): DynamicPropertyItem[] {
    const normalizedGroupKeys = new Set(
      groupKeys.map((key) => this.normalizarNomePropriedade(key)),
    );

    return this.propriedadesFormArray.controls
      .map((ctrl, index) => ({ ctrl, index }))
      .filter((item) => {
        const nome = this.normalizarNomePropriedade(
          item.ctrl.get('nome')?.value,
        );

        return normalizedGroupKeys.has(nome);
      });
  }

  private normalizarNomePropriedade(value: unknown): string {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .replace(/[\s-]+/g, '_')
      .toUpperCase();
  }

  getUnassignedProps(): DynamicPropertyItem[] {
    if (!this.isViatura) {
      return this.propriedadesFormArray.controls.map((ctrl, index) => ({
        ctrl,
        index,
      }));
    }
    const allAssigned = [
      ...this.viaturaProps.identificacao,
      ...this.viaturaProps.manutencao,
      ...this.viaturaProps.seguro,
      ...this.viaturaProps.responsavel,
    ];
    return this.propriedadesFormArray.controls
      .map((ctrl, index) => ({ ctrl, index }))
      .filter((item) => {
        const nome = this.normalizarNomePropriedade(
          item.ctrl.get('nome')?.value,
        );

        return !allAssigned.includes(nome);
      });
  }

  getPropertyValueControl(item: DynamicPropertyItem): FormControl {
    return item.ctrl.get('valor') as FormControl;
  }

  getCampoLabel(item: DynamicPropertyItem): string {
    const nome = this.normalizarNomePropriedade(item.ctrl.get('nome')?.value);

    if (this.isArma) {
      const labelsArma: Record<string, string> = {
        COR_ACABAMENTO: 'Cor/Acabamento',
        CATEGORIA_EQUIPAMENTO_ARMA: 'Categoria',
        PROXIMA_VERIFICACAO_OBRIGATORIA: 'Próxima Verificação',
      };

      if (labelsArma[nome]) {
        return labelsArma[nome];
      }
    }

    return this.viaturaLabels[nome] ?? formatNomeCampo(nome);
  }

  trackByPropertyIndex(_position: number, item: DynamicPropertyItem): number {
    return item.index;
  }

  ngOnInit(): void {
    this.initForm();

    const id = this.drawerMode ? null : this.route.snapshot.paramMap.get('id');
    if (id) {
      this.editMode = true;
      this.editId = +id;
    }

    this.loadDropdowns();
    this.postoSvc
      .getAll({ pageSize: 200 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const items = res?.data ?? res ?? [];
          this.postos = Array.isArray(items)
            ? items
                .map((posto: any) => ({
                  ...posto,
                  id: Number(posto?.id ?? posto?.Id),
                }))
                .filter((posto: Posto) => Number.isFinite(posto.id) && Number(posto.id) > 0)
            : [];
        },
      });
  }

  initForm(): void {
    this.form = this.fb.group({
      idTipoEquipamento: [null, Validators.required],
      tipoEquipamentoEnum: [null, Validators.required],
      estado: [EnumEstadoEquipamento.BOM, Validators.required],
      dataAquisicao: [null],
      valorAquisicao: [null],
      idPosto: [null],
      marcaUtensilio: [null],
      modeloUtensilio: [null],
      observacoes: [''],
      propriedades: this.fb.array([]),
    });
  }

  onPostoSelecionado(event: any): void {
    const valor = event?.value ?? event;
    const idPosto = valor == null || valor === '' ? null : Number(valor);
    this.form.get('idPosto')?.setValue(
      idPosto != null && Number.isFinite(idPosto) && idPosto > 0 ? idPosto : null,
    );
  }

  hasDynamicProperty(nome: string): boolean {
    const target = this.normalizarNomePropriedade(nome);
    return this.propriedadesFormArray.controls.some(
      (control) => this.normalizarNomePropriedade(control.get('nome')?.value) === target,
    );
  }

  onMarcaUtensilioChange(event: any): void {
    const marcaId = Number(event?.value ?? event);
    this.form.patchValue({ modeloUtensilio: null }, { emitEvent: false });
    this.modelosUtensilio = [];
    if (!Number.isFinite(marcaId) || marcaId <= 0) return;

    this.loadingModelosUtensilio = true;
    this.dataSourceRegistry
      .resolve('MODELOS_POR_MARCA', marcaId, true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (options) => {
          this.modelosUtensilio = options;
          this.loadingModelosUtensilio = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.modelosUtensilio = [];
          this.loadingModelosUtensilio = false;
          this.cdr.detectChanges();
        },
      });
  }

  onMarcaUtensilioCriada(result: QuickCreateOptionResult): void {
    this.marcasUtensilio = result.options;
    if (result.selectedValue !== null && result.selectedValue !== undefined) {
      this.form.get('marcaUtensilio')?.setValue(result.selectedValue);
      this.onMarcaUtensilioChange({ value: result.selectedValue });
    }
  }

  onModeloUtensilioCriado(result: QuickCreateOptionResult): void {
    this.modelosUtensilio = result.options;
    if (result.selectedValue !== null && result.selectedValue !== undefined) {
      this.form.get('modeloUtensilio')?.setValue(result.selectedValue);
    }
  }

  private carregarMarcasUtensilio(): void {
    this.loadingMarcasUtensilio = true;
    this.dataSourceRegistry
      .resolve('MARCAS_UTENSILIO', EnumMarcaCategoria.UTENSILIO, true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (options) => {
          this.marcasUtensilio = options;
          this.loadingMarcasUtensilio = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.marcasUtensilio = [];
          this.loadingMarcasUtensilio = false;
          this.cdr.detectChanges();
        },
      });
  }

  onTipoEquipamentoChange(
    tipoId: number | null,
    existingValues?: EquipamentoPropriedadeValor[],
  ): void {
    const arr = this.propriedadesFormArray;
    arr.clear();
    this.selecionarCategoriaDoTipo(tipoId);

    if (!tipoId) return;
    if (this.isUtensilio) this.carregarMarcasUtensilio();

    this.loadingPropriedades = true;
    this.cdr.detectChanges();

    this.propriedadeSvc
      .getByTipoEquipamento(tipoId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const defs: PropriedadeEquipamento[] = res.data ?? [];
          this.montarPropriedadesDinamicas(defs, existingValues);
        },
        error: () => {
          this.loadingPropriedades = false;
          this.cdr.detectChanges();
        },
      });
  }

  private montarPropriedadesDinamicas(
    defs: PropriedadeEquipamento[],
    existingValues?: EquipamentoPropriedadeValor[],
  ): void {
    const arr = this.propriedadesFormArray;
    defs.forEach((prop) => {
            let valor: any = null;
            const nomeNorm = this.normalizarNomePropriedade(prop.nome);
            const isPostoProp = nomeNorm === 'POSTO_ATRIBUIDO' || nomeNorm === 'POSTO';

            if (existingValues?.length) {
              const found = existingValues.find(
                (ev) => ev.propriedadeId === prop.id,
              );
              if (found) {
                if (prop.tipoDado === 4 && found.valor) {
                  valor = new Date(found.valor);
                } else if (prop.tipoDado === 2 && found.valor) {
                  valor = Number(found.valor);
                } else {
                  valor = found.valor;
                }

                if (isPostoProp && !this.form.get('idPosto')?.value && found.valor) {
                  const numVal = Number(found.valor);
                  if (Number.isFinite(numVal) && numVal > 0) {
                    this.form.patchValue({ idPosto: numVal }, { emitEvent: false });
                  }
                }
              }
            }

            if (isPostoProp && !valor && this.form.get('idPosto')?.value) {
              valor = String(this.form.get('idPosto')?.value);
            }
            const sourceKey =
              this.isArma && nomeNorm === 'CALIBRE'
                ? PROPRIEDADE_OPCOES_SOURCE_KEY
                : this.isUtensilio && nomeNorm === 'MARCA'
                ? 'MARCAS_UTENSILIO'
                : this.isUtensilio && nomeNorm === 'MODELO'
                  ? 'MODELOS_POR_MARCA'
                  : prop.sourceKey ??
                    (this.isListaProperty(prop)
                      ? PROPRIEDADE_OPCOES_SOURCE_KEY
                      : null);
            arr.push(
              this.fb.group({
                propriedadeId: [prop.id],
                nome: [prop.nome],
                tipoDado: [prop.tipoDado],
                icon: [prop.icon ?? 'pi pi-list'],
                sourceKey: [sourceKey],
                valor: [valor],
              }),
            );
    });
    this.setupPropriedadesDinamicas(defs);
    this.loadingPropriedades = false;
    this.cdr.detectChanges();
  }

  onCategoriaChange(categoria: EnumMarcaCategoria | null): void {
    if (categoria == null) return;
    this.propriedadesFormArray.controls.forEach((control, i) => {
      const sourceKey = control.get('sourceKey')?.value;
      if (sourceKey && String(sourceKey).startsWith('MARCAS_')) {
        this.propDropdownDisabled[i] = false;
        this.loadPropOptions(i, sourceKey, categoria, true);
      }
    });
  }

  private setupPropriedadesDinamicas(defs: PropriedadeEquipamento[]): void {
    const arr = this.propriedadesFormArray;
    this.propOptions = defs.map(() => []);
    this.propOptionsLoading = defs.map(() => false);
    this.propDropdownDisabled = defs.map(() => false);

    defs.forEach((prop, i) => {
      const nome = this.normalizarNomePropriedade(prop.nome);
      const sourceKey =
        this.isArma && nome === 'CALIBRE'
          ? PROPRIEDADE_OPCOES_SOURCE_KEY
          : this.isUtensilio && nome === 'MARCA'
          ? 'MARCAS_UTENSILIO'
          : this.isUtensilio && nome === 'MODELO'
            ? 'MODELOS_POR_MARCA'
            : prop.sourceKey ??
              (this.isListaProperty(prop)
                ? PROPRIEDADE_OPCOES_SOURCE_KEY
                : null);
      if (!sourceKey) return;

      if (sourceKey === PROPRIEDADE_OPCOES_SOURCE_KEY) {
        this.loadPropOptions(i, sourceKey, prop.id, !!prop.cache);
        return;
      }

      if (sourceKey.startsWith('MARCAS_')) {
        const categoria = this.form.get('tipoEquipamentoEnum')?.value;
        this.propDropdownDisabled[i] = categoria == null;
        if (categoria != null) {
          this.loadPropOptions(i, sourceKey, categoria, !!prop.cache);
        }
        return;
      }

      const dependeDe =
        this.isUtensilio && nome === 'MODELO'
          ? 'MARCA'
          : prop.dependeDe ?? null;
      if (!dependeDe) {
        this.loadPropOptions(i, sourceKey, undefined, !!prop.cache);
        return;
      }

      const parentIndex = defs.findIndex(
        (definition) => this.normalizarNomePropriedade(definition.nome) === this.normalizarNomePropriedade(dependeDe),
      );
      if (parentIndex === -1) {
        this.propDropdownDisabled[i] = true;
        console.warn(
          `[NovoEquipamento] dependeDe="${dependeDe}" nao encontrado para "${prop.nome}"`,
        );
        return;
      }

      const parentCtrl = arr.at(parentIndex).get('valor');
      const childCtrl = arr.at(i).get('valor');

      this.propDropdownDisabled[i] = !parentCtrl?.value;
      if (parentCtrl?.value) {
        this.loadPropOptions(i, sourceKey, parentCtrl.value, !!prop.cache);
      }

      parentCtrl?.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((newParentValue) => {
          childCtrl?.setValue(null, { emitEvent: false });
          this.propOptions[i] = [];
          if (newParentValue) {
            this.propDropdownDisabled[i] = false;
            this.loadPropOptions(
              i,
              sourceKey,
              newParentValue,
              !!prop.cache,
            );
          } else {
            this.propDropdownDisabled[i] = true;
          }
          this.cdr.detectChanges();
        });
    });
  }

  private loadPropOptions(
    i: number,
    sourceKey: string,
    dependValue: any,
    useCache: boolean,
  ): void {
    this.propOptionsLoading[i] = true;
    this.dataSourceRegistry
      .resolve(sourceKey, dependValue, useCache)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (options) => {
          this.propOptions[i] = this.combinarOpcoesCalibre(i, options);

          const valueCtrl = this.propriedadesFormArray.at(i)?.get('valor');
          const currentValue = valueCtrl?.value;
          if (
            currentValue !== null &&
            currentValue !== undefined &&
            currentValue !== ''
          ) {
            const selected = this.propOptions[i].find(
              (option) => String(option.value) === String(currentValue),
            );
            if (selected && selected.value !== currentValue) {
              valueCtrl?.setValue(selected.value, { emitEvent: false });
            }
          }

          this.propOptionsLoading[i] = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.propOptions[i] = [];
          this.propOptionsLoading[i] = false;
          this.cdr.detectChanges();
        },
      });
  }

  canCreateDynamicOption(sourceKey: string | null | undefined): boolean {
    return (
      !!sourceKey &&
      (sourceKey.startsWith('MARCAS_') ||
        sourceKey === 'MODELOS_POR_MARCA' ||
        sourceKey === PROPRIEDADE_OPCOES_SOURCE_KEY)
    );
  }

  private combinarOpcoesCalibre(index: number, options: DropdownOption[]): DropdownOption[] {
    const nome = this.normalizarNomePropriedade(
      this.propriedadesFormArray.at(index)?.get('nome')?.value,
    );
    if (!this.isArma || nome !== 'CALIBRE') return options;

    const combined = [...options];
    for (const calibre of CALIBRES_OPTIONS) {
      if (!combined.some((item) => String(item.value).toLowerCase() === String(calibre.value).toLowerCase())) {
        combined.push(calibre);
      }
    }
    return combined;
  }

  private isListaProperty(prop: PropriedadeEquipamento): boolean {
    return (
      Number(prop.tipoDado) === 6 ||
      this.normalizarTexto(prop.tipoDadoDescricao ?? '') === 'LISTA'
    );
  }

  getCreateDependValue(
    index: number,
    sourceKey: string | null | undefined,
  ): number | null {
    if (!sourceKey) return null;

    if (sourceKey === PROPRIEDADE_OPCOES_SOURCE_KEY) {
      return (
        this.propriedadesFormArray.at(index)?.get('propriedadeId')?.value ??
        null
      );
    }

    if (sourceKey.startsWith('MARCAS_')) {
      return this.form.get('tipoEquipamentoEnum')?.value ?? null;
    }

    if (sourceKey === 'MODELOS_POR_MARCA') {
      const marcaControl = this.propriedadesFormArray.controls.find(
        (control) =>
          String(control.get('sourceKey')?.value ?? '').startsWith('MARCAS_') ||
          this.normalizarTexto(control.get('nome')?.value ?? '') === 'MARCA',
      );
      return marcaControl?.get('valor')?.value ?? null;
    }

    return this.propriedadesFormArray.at(index)?.get('valor')?.value ?? null;
  }

  onDynamicOptionCreated(index: number, result: QuickCreateOptionResult): void {
    this.propOptions[index] = this.combinarOpcoesCalibre(index, result.options);
    if (result.selectedValue !== null && result.selectedValue !== undefined) {
      this.propriedadesFormArray
        .at(index)
        .get('valor')
        ?.setValue(result.selectedValue);
    }
    this.cdr.detectChanges();
  }

  readonly formatNomeCampo = formatNomeCampo;

  loadEquipamento(id: number): void {
    this.loading = true;
    this.svc
      .getById(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const eq = res?.data ?? res;
          const tipoId = eq.idTipoEquipamento ?? eq.tipoEquipamento?.id ?? null;
          const propPosto = (eq.propriedades ?? eq.Propriedades ?? [])?.find((p: any) => {
            const nome = this.normalizarNomePropriedade(p.nome ?? p.Nome);
            return nome === 'POSTO_ATRIBUIDO' || nome === 'POSTO';
          });
          const posto = eq.posto ?? eq.Posto;
          const rawPostoId =
            eq.idPosto ??
            eq.IdPosto ??
            eq.postoId ??
            eq.PostoId ??
            (posto?.id > 0 ? posto.id : null) ??
            (posto?.Id > 0 ? posto.Id : null) ??
            propPosto?.valor ??
            propPosto?.Valor;
          const postoId = Number(rawPostoId);
          this.form.patchValue({
            idTipoEquipamento: tipoId,
            tipoEquipamentoEnum: eq.tipoEquipamentoEnum ?? null,
            estado: eq.estado ?? EnumEstadoEquipamento.BOM,
            dataAquisicao: eq.dataAquisicao ? new Date(eq.dataAquisicao) : null,
            valorAquisicao: eq.valorAquisicao ?? null,
            idPosto: Number.isFinite(postoId) && postoId > 0 ? postoId : null,
            observacoes: eq.observacoes ?? '',
          });
          this.form.get('idTipoEquipamento')?.disable({ emitEvent: false });

          const rawFoto = eq.fotoUrl ?? eq.foto ?? eq.caminhoFoto ?? null;
          if (rawFoto) {
            this.fotoUrlOriginal = rawFoto;
            this.fotoPreviewUrl = rawFoto.startsWith('http')
              ? rawFoto
              : `${environment.apiurl}/${rawFoto.replace(/^\//, '')}`;
          }

          if (tipoId) {
            this.onTipoEquipamentoChange(tipoId, eq.propriedades ?? []);
          }

          this.loading = false;
        },
        error: () => {
          this.msg.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Equipamento não encontrado.',
            life: 3000,
          });
          this.loading = false;
          this.router.navigate(['..'], { relativeTo: this.route });
        },
      });
  }

  loadDropdowns(): void {
    this.loadingDropdowns = true;
    forkJoin({
      tipos: this.tipoSvc.getAll({
        pageSize: 200,
        ...(this.IdCategoriaEquipamento
          ? { IdCategoriaEquipamento: this.IdCategoriaEquipamento }
          : {}),
      }),
      categorias: this.enumSvc.getEnum('MarcaCategoria'),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ tipos, categorias }) => {
          this.tiposEquipamento = tipos.data ?? [];
          this.categorias = Object.entries(categorias).map(
            ([value, label]) => ({
              label,
              value: Number(value) as EnumMarcaCategoria,
            }),
          );
          this.loadingDropdowns = false;

          if (this.editMode && this.editId) {
            this.loadEquipamento(this.editId);
          } else {
            this.applyTipoFromQueryParam();
          }
        },
        error: () => {
          this.loadingDropdowns = false;
        },
      });
  }

  private selecionarCategoriaDoTipo(tipoId: number | null): void {
    const categoriaControl = this.form.get('tipoEquipamentoEnum');
    if (!tipoId) {
      categoriaControl?.setValue(null);
      return;
    }

    const tipo = this.tiposEquipamento.find((item) => item.id === tipoId);
    if (!tipo) return;

    const designacao = this.normalizarTexto(tipo.designacao);
    let categoriaEsperada: EnumMarcaCategoria | null = null;
    if (/VIATUR|VEICUL/.test(designacao)) {
      categoriaEsperada = EnumMarcaCategoria.VEICULO;
    } else if (/ARMA/.test(designacao)) {
      categoriaEsperada = EnumMarcaCategoria.ARMA;
    } else if (/UTENSIL/.test(designacao)) {
      categoriaEsperada = EnumMarcaCategoria.UTENSILIO;
    }

    const categoriaPorDesignacao = this.categorias.find(
      (categoria) => categoria.value === categoriaEsperada,
    );
    const nomeCategoria = this.normalizarTexto(tipo.nomeCategoria ?? '');
    const categoriaPorNome = this.categorias.find(
      (categoria) => this.normalizarTexto(categoria.label) === nomeCategoria,
    );
    const categoriaPorId = this.categorias.find(
      (categoria) => categoria.value === tipo.idCategoriaEquipamento,
    );

    categoriaControl?.setValue(
      categoriaPorDesignacao?.value ??
        categoriaPorId?.value ??
        categoriaPorNome?.value ??
        null,
    );
  }

  private normalizarTexto(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toUpperCase();
  }

  private applyTipoFromQueryParam(): void {
    const routeTipo = this.route.snapshot.queryParamMap.get('tipo');
    const tipoParam =
      this.presetTipoId ?? (routeTipo ? Number(routeTipo) : null);
    if (tipoParam == null) return;

    const tipoId = Number(tipoParam);
    const tipoExiste = this.tiposEquipamento.some((t) => t.id === tipoId);

    if (!tipoExiste) {
      console.warn(
        `[NovoEquipamento] tipo id=${tipoId} (via queryParam) não encontrado na lista de tipos da API.`,
      );
      return;
    }

    this.tipoPreSelecionado = true;
    this.form.patchValue({
      idTipoEquipamento: tipoId,
    });
    this.form.get('idTipoEquipamento')?.disable({ emitEvent: false });
    this.onTipoEquipamentoChange(tipoId);
    this.cdr.detectChanges();
  }

  // ── Foto ────────────────────────────────────────────────────────────────────

  async onFotoSelect(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    if (file.size > 5 * 1024 * 1024) {
      this.msg.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Imagem muito grande. Máx 5MB.',
        life: 3000,
      });
      return;
    }
    const compressedFile = await compressImageIfNeeded(file);
    this.selectedFoto = compressedFile;
    const reader = new FileReader();
    reader.onload = () => {
      this.fotoPreviewUrl = reader.result as string;
      this.cdr.detectChanges();
    };
    reader.readAsDataURL(compressedFile);
  }

  removeFoto(): void {
    this.selectedFoto = null;
    this.fotoPreviewUrl = null;
  }

  // ── Documentos ──────────────────────────────────────────────────────────────

  addDocumento(): void {
    this.pendingDocumentos.push({ file: null, designacao: '' });
  }

  removeDocumento(i: number): void {
    this.pendingDocumentos.splice(i, 1);
  }

  onDocumentoSelect(event: Event, i: number): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    if (file.size > 20 * 1024 * 1024) {
      this.msg.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Ficheiro muito grande. Máx 20MB.',
        life: 3000,
      });
      return;
    }
    this.pendingDocumentos[i].file = file;
    if (!this.pendingDocumentos[i].designacao) {
      this.pendingDocumentos[i].designacao = file.name.replace(/\.[^.]+$/, '');
    }
  }

  // ── Manutenções ─────────────────────────────────────────────────────────────

  addManutencao(): void {
    this.pendingManutencoes.push({
      tipo: 0,
      data: null,
      tecnico: '',
      descricao: '',
      custo: null,
    });
  }

  removeManutencao(i: number): void {
    this.pendingManutencoes.splice(i, 1);
  }

  cancel(): void {
    if (this.drawerMode) {
      this.closed.emit();
      return;
    }

    this.router.navigate(['/admin/equipamentos']);
  }

  // ── Submit ──────────────────────────────────────────────────────────────────

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (!this.form.getRawValue().idTipoEquipamento) {
      this.msg.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Selecione o tipo de equipamento.',
        life: 3000,
      });
      return;
    }

    if (this.submitted || this.loading) return;
    this.submitted = true;
    this.loading = true;

    const v = this.form.getRawValue();
    const fd = new FormData();

    fd.append('IdTipoEquipamento', String(v.idTipoEquipamento));
    fd.append('TipoEquipamentoEnum', String(v.tipoEquipamentoEnum));
    fd.append('Estado', String(v.estado));

    const idPosto = v.idPosto == null || v.idPosto === '' ? null : Number(v.idPosto);
    const idPostoVal = idPosto != null && Number.isFinite(idPosto) && idPosto > 0
      ? String(idPosto)
      : null;
    if (idPostoVal) {
      // O contrato de Equipamentos usa idPosto tanto no POST como no PUT.
      fd.append('IdPosto', idPostoVal);
    }
    if (v.dataAquisicao)
      fd.append(
        'DataAquisicao',
        new Date(v.dataAquisicao).toISOString().split('T')[0],
      );
    if (v.valorAquisicao != null)
      fd.append('ValorAquisicao', String(v.valorAquisicao));
    if (v.observacoes) fd.append('Observacoes', v.observacoes);

    if (this.isUtensilio) {
      const marca = this.marcasUtensilio.find(
        (option) => String(option.value) === String(v.marcaUtensilio),
      );
      const modelo = this.modelosUtensilio.find(
        (option) => String(option.value) === String(v.modeloUtensilio),
      );
      if (!this.hasDynamicProperty('MARCA') && marca) fd.append('Marca', String(marca.value));
      if (!this.hasDynamicProperty('MODELO') && modelo) fd.append('Modelo', String(modelo.value));
    }

    let propIdx = 0;
    (v.propriedades as any[]).forEach((p) => {
      let valorFinal = p.valor;
      const nomeProp = this.normalizarNomePropriedade(p.nome);
      if (
        (nomeProp === 'POSTO_ATRIBUIDO' || nomeProp === 'POSTO') &&
        idPostoVal
      ) {
        valorFinal = idPostoVal;
      }
      if (valorFinal === null || valorFinal === undefined || valorFinal === '') return;
      let valorStr: string;
      if (p.tipoDado === 4 && valorFinal instanceof Date) {
        valorStr = valorFinal.toISOString().split('T')[0];
      } else {
        valorStr = String(valorFinal);
      }
      fd.append(
        `Propriedades[${propIdx}].PropriedadeId`,
        String(p.propriedadeId),
      );
      fd.append(`Propriedades[${propIdx}].Valor`, valorStr);
      propIdx++;
    });

    if (this.editMode) {
      fd.append('Ativo', 'true');
    } else {
      this.pendingDocumentos
        .filter((d) => d.file)
        .forEach((d, i) => {
          fd.append(`Documentos[${i}].Descricao`, d.designacao || d.file!.name);
          fd.append(`Documentos[${i}].Ficheiro`, d.file!, d.file!.name);
        });
      this.pendingManutencoes
        .filter((m) => m.descricao || m.tecnico)
        .forEach((m, i) => {
          fd.append(
            `Manutencoes[${i}].TipoManutencaoEquipamentoEnum`,
            String(m.tipo),
          );
          if (m.data)
            fd.append(
              `Manutencoes[${i}].Data`,
              new Date(m.data).toISOString().split('T')[0],
            );
          fd.append(`Manutencoes[${i}].TecResponsavel`, m.tecnico);
          fd.append(`Manutencoes[${i}].Custo`, String(m.custo ?? 0));
          fd.append(`Manutencoes[${i}].Descricao`, m.descricao);
        });
    }

    const op =
      this.editMode && this.editId
        ? this.svc.updateWithFormData(this.editId, fd)
        : this.svc.createWithFormData(fd);

    // 1. Guarda ou actualiza o equipamento primeiro
    op.pipe(
      switchMap((res: any) => {
        const eq = res?.data ?? res;
        const targetId = eq?.id ?? this.editId;

        // Se houver uma foto seleccionada, faz upload para POST /api/Equipamentos/{id}/foto.
        if (this.selectedFoto && targetId) {
          return this.svc.uploadFoto(targetId, this.selectedFoto).pipe(
            switchMap((fotoRes: any) => of({ eq, fotoRes })),
          );
        }

        return of({ eq, fotoRes: null });
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: ({ eq, fotoRes }) => {
        if (this.editMode && this.fotoUrlOriginal) {
          this.equipmentPhotoService.clear(this.fotoUrlOriginal);
        }

        const rawFoto = fotoRes?.fotoUrl ?? fotoRes?.foto ?? eq?.fotoUrl ?? eq?.foto;
        if (rawFoto) {
          this.fotoPreviewUrl = rawFoto.startsWith('http')
            ? `${rawFoto}?t=${new Date().getTime()}`
            : `${environment.apiurl}/${rawFoto.replace(/^\//, '')}?t=${new Date().getTime()}`;
        }

        this.msg.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Equipamento guardado com sucesso.',
          life: 3000,
        });

        if (this.drawerMode) {
          this.saved.emit(eq);
          this.closed.emit();
          return;
        }

        setTimeout(
          () => this.router.navigate(['/admin/equipamentos', eq?.id ?? this.editId]),
          1200,
        );
      },
      error: (err: any) => {
        this.loading = false;
        this.submitted = false;
        this.msg.add({
          severity: 'error',
          summary: 'Erro',
          detail: err?.error?.message || err?.message || 'Falha ao guardar equipamento.',
          life: 4000,
        });
      },
    });
  }
}
